import React, { useCallback, useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import firebase from "../../../../../database/firebase";
import DB_PATH from "../../../../../database/DB_PATH";
import {
  StudioLayout,
  GenerationsRail,
  GenerationSession,
  InspectorPanel,
  InspectorSection,
  PromptComposer,
  Tag,
  Button,
} from "../../../../../design-system";
import { Plan } from "../../../../../types/Plan";
import { DayPlan } from "../../../../../types/DayPlan";
import { DailyStep } from "../../../../../types/DailyStep";
import SlideGenerator from "../../../plan/dayplan/components/dailyStep/SlideGenerator";
import ViralReelCreator from "../../../plan/dayplan/components/dailyStep/ViralReelCreator";
import StockFootageReelGenerator from "../../../plan/dayplan/components/dailyStep/StockFootageReelGenerator";
import {
  GEMINI_API_KEY,
  UNSPLASH_ACCESS_KEY,
  DEFAULT_ELEVENLABS_API_KEY,
  DEFAULT_SEEDANCE_API_KEY,
  DEFAULT_PEXELS_API_KEY,
  DEFAULT_PIXABAY_API_KEY,
  DEFAULT_JAMENDO_CLIENT_ID,
} from "../../../plan/dayplan/components/dailyStep/mediaGeneratorConfig";
import { AudioFile } from "../../../plan/dayplan/components/dailyStep/types";
import { usePlanStepSelection } from "../../hooks/usePlanStepSelection";
import { useMediaStudio } from "../../context/MediaStudioContext";

// The step-bound generation studio: pick a Plan -> Day -> Step, then run one
// of the shared generators (the exact components the day-plan editor uses)
// against that step's script. One flow at a time, chosen in the Create hub.

export type StepFlow = "slides" | "viral" | "free";

export const STEP_FLOW_META: Record<
  StepFlow,
  { label: string; description: string }
> = {
  slides: {
    label: "Slides",
    description: "Carousel slides generated from the step script.",
  },
  viral: {
    label: "Viral reel",
    description: "AI-directed reel with voice narration and generated scenes.",
  },
  free: {
    label: "Free reel",
    description: "Stock-footage reel cut to the step script.",
  },
};

const TopBarRoot = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--space-3);
  flex: 1 1 0;
  min-width: 0;
`;

const TopBarTitle = styled.div`
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
`;

const TopBarHeading = styled.div`
  font-family: var(--font-display);
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text);
`;

const TopBarSub = styled.div`
  font-family: var(--font-sans);
  font-size: var(--font-size-xs);
  color: var(--color-text-secondary);
`;

const SelectField = styled.select`
  appearance: none;
  height: 32px;
  padding: 0 var(--space-3);
  background: var(--color-surface);
  border: 1px solid var(--color-border-subtle);
  border-radius: var(--radius-md);
  color: var(--color-text);
  font-family: var(--font-sans);
  font-size: var(--font-size-xs);
  min-width: 0;
  flex: 1 1 0;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: var(--color-accent);
    box-shadow: 0 0 0 3px var(--color-focus-ring);
  }

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

const RailPickers = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  margin-bottom: var(--space-2);
`;

const RailPickerLabel = styled.label`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const RailPickerLabelText = styled.span`
  font-family: var(--font-sans);
  font-size: 10px;
  font-weight: var(--font-weight-semibold);
  color: var(--color-text-tertiary);
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

const CanvasFrame = styled.div`
  width: 100%;
  max-width: 1200px;
  background: var(--color-surface);
  border: 1px solid var(--color-border-subtle);
  border-radius: var(--radius-xl);
  box-shadow: var(--shadow-md);
  padding: var(--space-5);
  min-height: 60vh;
`;

const EmptyCanvas = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: var(--space-2);
  padding: var(--space-12) var(--space-6);
  text-align: center;
  color: var(--color-text-secondary);
  font-family: var(--font-sans);
`;

const EmptyTitle = styled.div`
  font-family: var(--font-display);
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text);
`;

const EmptyHint = styled.div`
  font-size: var(--font-size-sm);
  max-width: 44ch;
  line-height: var(--line-height-base);
`;

const StatusStack = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-1);
`;

const pickStepThumb = (
  step: DailyStep
): { kind: "image" | "video"; src?: string; posterSrc?: string } => {
  if (step.videoURL) {
    return { kind: "video", src: step.videoURL, posterSrc: step.backgroundImageURL };
  }
  const slide = (step.slides || []).find((s) => s.options && s.options.length > 0);
  if (slide) {
    const opt = slide.options[slide.selectedIndex] || slide.options[0];
    if (opt?.imageUrl) return { kind: "image", src: opt.imageUrl };
  }
  const seg = (step.reelSegments || []).find((s) => s.options && s.options.length > 0);
  if (seg) {
    const opt = seg.options[seg.selectedIndex] || seg.options[0];
    if (opt?.imageUrl) return { kind: "image", src: opt.imageUrl };
  }
  if (step.backgroundImageURL) {
    return { kind: "image", src: step.backgroundImageURL };
  }
  return { kind: "image" };
};

const formatStepLabel = (step: DailyStep): string => {
  const tags: string[] = [];
  if ((step.reelSegments || []).length > 0) tags.push("Viral");
  if ((step.slides || []).length > 0) tags.push("Slides");
  if (step.videoURL) tags.push("Reel");
  const tagStr = tags.length ? ` · ${tags.join(" + ")}` : "";
  const position =
    typeof step.position === "number" && Number.isFinite(step.position)
      ? `#${step.position} `
      : "";
  return `${position}${step.title || "Untitled"}${tagStr}`;
};

const emptyHintFor = (props: {
  loadingPlans: boolean;
  plans: Plan[];
  selectedPlanId: string;
  loadingDayPlans: boolean;
  dayPlans: DayPlan[];
  selectedDayPlanId: string;
  loadingSteps: boolean;
  steps: DailyStep[];
}): string => {
  if (props.loadingPlans) return "Loading plans...";
  if (props.plans.length === 0)
    return "No plans found. Create a plan first, then come back here.";
  if (!props.selectedPlanId) return "Select a plan in the top bar to begin.";
  if (props.loadingDayPlans) return "Loading days...";
  if (props.dayPlans.length === 0) return "This plan has no days yet.";
  if (!props.selectedDayPlanId) return "Pick a day to continue.";
  if (props.loadingSteps) return "Loading steps...";
  if (props.steps.length === 0) return "This day has no steps yet.";
  return "Pick a step from the history rail to start generating.";
};

export type StepContentStudioProps = {
  flow: StepFlow;
  onBack: () => void;
};

export const StepContentStudio: React.FC<StepContentStudioProps> = ({
  flow,
  onBack,
}) => {
  const { user, community } = useMediaStudio();
  const selection = usePlanStepSelection(user?.communityId);
  const {
    plans,
    dayPlans,
    steps,
    planSteps,
    loadingPlans,
    loadingDayPlans,
    loadingSteps,
    selectedPlanId,
    selectedDayPlanId,
    selectedStepId,
    selectPlanId,
    selectDayPlanId,
    selectStepId,
    selectedPlan,
    selectedStep,
    patchStepField,
  } = selection;

  const [textContent, setTextContent] = useState("");
  const [textStepId, setTextStepId] = useState("");
  const [isSavingText, setIsSavingText] = useState(false);

  // ── Audio files (for Free Reel) ──
  const [audioFiles, setAudioFiles] = useState<AudioFile[]>([]);
  useEffect(() => {
    if (flow !== "free" || !selectedStepId || !user?.communityId) {
      setAudioFiles([]);
      return;
    }
    const storage = firebase.storage();
    const listRef = storage.ref(
      `${DB_PATH.AUDIO}/${user.communityId}/${selectedStepId}`
    );
    listRef
      .listAll()
      .then(async (result) => {
        const files: AudioFile[] = await Promise.all(
          result.items.map(async (itemRef) => {
            const url = await itemRef.getDownloadURL();
            return { name: itemRef.name, url, fullPath: itemRef.fullPath };
          })
        );
        files.sort((a, b) => {
          const numA = parseInt(a.name.replace(/\D/g, ""), 10) || 0;
          const numB = parseInt(b.name.replace(/\D/g, ""), 10) || 0;
          return numA - numB;
        });
        setAudioFiles(files);
      })
      .catch(() => setAudioFiles([]));
  }, [flow, selectedStepId, user?.communityId]);

  // ── Sync text when step changes ──
  useEffect(() => {
    if (!selectedStep) {
      setTextContent("");
      setTextStepId("");
      return;
    }
    if (selectedStep.id !== textStepId) {
      setTextContent(selectedStep.text || "");
      setTextStepId(selectedStep.id);
    }
  }, [selectedStep, textStepId]);

  const handleSetFieldValue = useCallback(
    (field: string, value: any) => {
      if (!selectedStepId) return;
      patchStepField(selectedStepId, field, value);
      if (field === "text") setTextContent(value || "");
    },
    [selectedStepId, patchStepField]
  );

  const saveTextToStep = async () => {
    if (!selectedStep) return;
    setIsSavingText(true);
    try {
      await firebase
        .firestore()
        .collection(DB_PATH.PLAN_STEP)
        .doc(selectedStep.id)
        .set({ text: textContent }, { merge: true });
      patchStepField(selectedStep.id, "text", textContent);
    } catch (error) {
      console.error("Error saving step text for media studio:", error);
    } finally {
      setIsSavingText(false);
    }
  };

  const canRender = !!selectedStep && !!user?.communityId;
  const primaryColor = community?.primaryColor || "#FFD700";
  const communityVoiceId = community?.author?.voiceId || "";

  const sessions: GenerationSession[] = useMemo(() => {
    if (!dayPlans.length) return [];

    const byDay = new Map<string, DailyStep[]>();
    planSteps.forEach((step) => {
      const dayId = step.dayPlanID || "unsorted";
      if (!byDay.has(dayId)) byDay.set(dayId, []);
      byDay.get(dayId)!.push(step);
    });

    byDay.forEach((arr) => {
      arr.sort((a, b) => {
        const pa =
          typeof a.position === "number" && Number.isFinite(a.position)
            ? a.position
            : Number.MAX_SAFE_INTEGER;
        const pb =
          typeof b.position === "number" && Number.isFinite(b.position)
            ? b.position
            : Number.MAX_SAFE_INTEGER;
        if (pa !== pb) return pa - pb;
        return (a.title || "").localeCompare(b.title || "");
      });
    });

    return dayPlans.map((dp) => {
      const dayStepsArr = byDay.get(dp.id) || [];
      const dayPos =
        typeof dp.position === "number" && Number.isFinite(dp.position)
          ? `Day ${dp.position}`
          : "Day";
      const dayTitle = dp.title ? `${dayPos} · ${dp.title}` : dayPos;
      return {
        id: dp.id,
        title: dayTitle,
        entries: dayStepsArr.map((step) => {
          const thumb = pickStepThumb(step);
          return {
            id: step.id,
            kind: thumb.kind,
            src: thumb.src,
            posterSrc: thumb.posterSrc,
            label: formatStepLabel(step),
          };
        }),
      };
    });
  }, [planSteps, dayPlans]);

  const promptChips = useMemo(() => {
    if (!selectedStep) return undefined;
    const chips: { id: string; label: string; icon?: React.ReactNode }[] = [];
    if (selectedPlan?.title)
      chips.push({ id: "plan", label: selectedPlan.title, icon: "📚" });
    if (selectedStep.title)
      chips.push({ id: "step", label: selectedStep.title, icon: "📍" });
    if (communityVoiceId)
      chips.push({ id: "voice", label: "Voice set", icon: "🎙" });
    return chips;
  }, [selectedPlan, selectedStep, communityVoiceId]);

  const wordCount = textContent.trim()
    ? textContent.trim().split(/\s+/).length
    : 0;

  const meta = STEP_FLOW_META[flow];

  return (
    <StudioLayout
      railWidth={300}
      inspectorWidth={380}
      topOffsetPx={150}
      topbar={
        <TopBarRoot>
          <Button variant="ghost" size="sm" onClick={onBack}>
            ← All formats
          </Button>
          <TopBarTitle>
            <TopBarHeading>{meta.label}</TopBarHeading>
            <TopBarSub>{meta.description}</TopBarSub>
          </TopBarTitle>
        </TopBarRoot>
      }
      rail={
        <GenerationsRail
          title="Step picker"
          sessions={sessions}
          activeEntryId={selectedStepId}
          headerSlot={
            <RailPickers>
              <RailPickerLabel>
                <RailPickerLabelText>Plan</RailPickerLabelText>
                <SelectField
                  aria-label="Plan"
                  value={selectedPlanId}
                  onChange={(e) => selectPlanId(e.target.value)}
                  disabled={loadingPlans || plans.length === 0}
                >
                  {loadingPlans ? (
                    <option value="">Loading plans...</option>
                  ) : plans.length === 0 ? (
                    <option value="">No plans</option>
                  ) : (
                    plans.map((plan) => (
                      <option key={plan.id} value={plan.id}>
                        {plan.title || "Untitled Plan"}
                      </option>
                    ))
                  )}
                </SelectField>
              </RailPickerLabel>
              <RailPickerLabel>
                <RailPickerLabelText>Day</RailPickerLabelText>
                <SelectField
                  aria-label="Day"
                  value={selectedDayPlanId}
                  onChange={(e) => selectDayPlanId(e.target.value)}
                  disabled={
                    !selectedPlanId || loadingDayPlans || dayPlans.length === 0
                  }
                >
                  {!selectedPlanId ? (
                    <option value="">Select plan</option>
                  ) : loadingDayPlans ? (
                    <option value="">Loading...</option>
                  ) : dayPlans.length === 0 ? (
                    <option value="">No days</option>
                  ) : (
                    dayPlans.map((dp) => (
                      <option key={dp.id} value={dp.id}>
                        {dp.title || "Untitled Day"}
                      </option>
                    ))
                  )}
                </SelectField>
              </RailPickerLabel>
              <RailPickerLabel>
                <RailPickerLabelText>Step</RailPickerLabelText>
                <SelectField
                  aria-label="Step"
                  value={selectedStepId}
                  onChange={(e) => selectStepId(e.target.value)}
                  disabled={
                    !selectedDayPlanId || loadingSteps || steps.length === 0
                  }
                >
                  {!selectedDayPlanId ? (
                    <option value="">Select day</option>
                  ) : loadingSteps ? (
                    <option value="">Loading...</option>
                  ) : steps.length === 0 ? (
                    <option value="">No steps</option>
                  ) : (
                    steps.map((step) => (
                      <option key={step.id} value={step.id}>
                        {step.title || "Untitled Step"}
                      </option>
                    ))
                  )}
                </SelectField>
              </RailPickerLabel>
            </RailPickers>
          }
          emptyHint={emptyHintFor({
            loadingPlans,
            plans,
            selectedPlanId,
            loadingDayPlans,
            dayPlans,
            selectedDayPlanId,
            loadingSteps,
            steps,
          })}
          onEntryClick={(entry) => {
            const step = planSteps.find((s) => s.id === entry.id);
            if (!step) {
              selectStepId(entry.id);
              return;
            }
            selectStepId(step.id);
            if (step.dayPlanID && step.dayPlanID !== selectedDayPlanId) {
              selectDayPlanId(step.dayPlanID);
            }
          }}
        />
      }
      inspector={
        <InspectorPanel
          title="Script & status"
          subtitle={
            selectedStep
              ? `${wordCount} word${wordCount === 1 ? "" : "s"}`
              : "Pick a step to edit its script"
          }
          footer={
            <Button
              variant="primary"
              size="md"
              isLoading={isSavingText}
              disabled={!selectedStep}
              onClick={saveTextToStep}
            >
              Save script
            </Button>
          }
        >
          <InspectorSection
            title="Step script"
            description="Drives slides, reels, and voice narration"
          >
            <PromptComposer
              value={textContent}
              onChange={setTextContent}
              placeholder="Paste or write the script that drives all generations for this step."
              disabled={!selectedStep}
              chips={promptChips}
              onSubmit={saveTextToStep}
            />
          </InspectorSection>

          {selectedStep && (
            <InspectorSection title="Status">
              <StatusStack>
                <Tag
                  variant={
                    (selectedStep.slides || []).length > 0 ? "success" : "neutral"
                  }
                  size="sm"
                  withDot
                >
                  Slides {(selectedStep.slides || []).length || ""}
                </Tag>
                <Tag
                  variant={
                    (selectedStep.reelSegments || []).length > 0
                      ? "success"
                      : "neutral"
                  }
                  size="sm"
                  withDot
                >
                  Viral Reel
                </Tag>
                <Tag
                  variant={selectedStep.videoURL ? "success" : "neutral"}
                  size="sm"
                  withDot
                >
                  Free Reel
                </Tag>
              </StatusStack>
            </InspectorSection>
          )}
        </InspectorPanel>
      }
    >
      <CanvasFrame>
        {!canRender ? (
          <EmptyCanvas>
            <EmptyTitle>Pick a step to start</EmptyTitle>
            <EmptyHint>
              {emptyHintFor({
                loadingPlans,
                plans,
                selectedPlanId,
                loadingDayPlans,
                dayPlans,
                selectedDayPlanId,
                loadingSteps,
                steps,
              })}
            </EmptyHint>
          </EmptyCanvas>
        ) : flow === "slides" && selectedStep ? (
          <SlideGenerator
            key={`slides-${selectedStep.id}`}
            text={textContent}
            geminiApiKey={GEMINI_API_KEY}
            unsplashAccessKey={UNSPLASH_ACCESS_KEY}
            stepId={selectedStep.id}
            communityId={user?.communityId}
            setFieldValue={handleSetFieldValue}
            existingSlides={selectedStep.slides || []}
            primaryColor={primaryColor}
          />
        ) : flow === "viral" && selectedStep ? (
          <ViralReelCreator
            key={`viral-${selectedStep.id}`}
            text={textContent}
            geminiApiKey={GEMINI_API_KEY}
            seedanceApiKey={DEFAULT_SEEDANCE_API_KEY}
            elevenLabsApiKey={DEFAULT_ELEVENLABS_API_KEY}
            voiceId={communityVoiceId}
            reelVoiceAuthorReference={selectedStep.reelVoiceAuthorReference || null}
            reelVoiceAudioUrl={selectedStep.reelVoiceAudioUrl || ""}
            reelVoiceTimeline={selectedStep.reelVoiceTimeline || []}
            stepId={selectedStep.id}
            stepTitle={selectedStep.title}
            stepSectionTitle={selectedPlan?.title}
            communityId={user?.communityId}
            setFieldValue={handleSetFieldValue}
            existingSegments={selectedStep.reelSegments || []}
          />
        ) : flow === "free" && selectedStep ? (
          <StockFootageReelGenerator
            key={`freereel-${selectedStep.id}`}
            text={textContent}
            audioFiles={audioFiles}
            pexelsApiKey={DEFAULT_PEXELS_API_KEY}
            pixabayApiKey={DEFAULT_PIXABAY_API_KEY}
            jamendoClientId={DEFAULT_JAMENDO_CLIENT_ID}
            geminiApiKey={GEMINI_API_KEY}
            stepId={selectedStep.id}
            communityId={user?.communityId}
            setFieldValue={handleSetFieldValue}
            videoURL={selectedStep.videoURL || ""}
          />
        ) : null}
      </CanvasFrame>
    </StudioLayout>
  );
};

export default StepContentStudio;
